import React, { useState, useEffect, useCallback, useRef } from 'react';
import './BuildServers.css';

const BuildServers = ({ teamcityApi, enableDebugging, showQueuedBuilds, watchedBuilds, setWatchedBuilds }) => {
  const [agents, setAgents] = useState([]);
  const [queuedBuilds, setQueuedBuilds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const previousBuildsRef = useRef({});

  // Test build state
  const [testBuild, setTestBuild] = useState(null);
  const testBuildIntervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!teamcityApi) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [agentsData, queueData] = await Promise.all([
        teamcityApi.getAgents(),
        teamcityApi.getQueuedBuilds()
      ]);
      // Sort agents alphabetically by name
      const sortedAgents = agentsData.sort((a, b) => a.name.localeCompare(b.name));
      setAgents(sortedAgents);
      setQueuedBuilds(queueData);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamcityApi]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 10 seconds for running builds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      return false;
    }
    if (Notification.permission === 'granted') {
      return true;
    }
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  };

  // Toggle watching a build
  const toggleWatchBuild = async (buildId, buildName) => {
    if (watchedBuilds[buildId]) {
      setWatchedBuilds(prev => {
        const next = { ...prev };
        delete next[buildId];
        return next;
      });
    } else {
      const hasPermission = await requestNotificationPermission();
      if (hasPermission) {
        setWatchedBuilds(prev => ({
          ...prev,
          [buildId]: { name: buildName, watchedAt: Date.now() }
        }));
      }
    }
  };

  // Send browser notification
  const sendNotification = (title, body, url) => {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: `${title}-${Date.now()}`,
      });
      notification.onclick = () => {
        window.focus();
        if (url) window.open(url, '_blank');
        notification.close();
      };
    }
  };

  // Check for build completions
  useEffect(() => {
    // Get all current build IDs from agents
    const currentBuildIds = new Set();
    agents.forEach(agent => {
      if (agent.build) {
        currentBuildIds.add(agent.build.id);
      }
    });

    // Check watched builds that were running but are no longer present
    Object.keys(watchedBuilds).forEach(buildId => {
      // Skip test builds (handled separately)
      if (buildId.startsWith('test-build-')) return;

      const prevBuildState = previousBuildsRef.current[buildId];

      // If we were tracking this build as running and it's no longer in the list, it completed
      if (prevBuildState && prevBuildState.state === 'running' && !currentBuildIds.has(Number(buildId)) && !currentBuildIds.has(buildId)) {
        const buildName = watchedBuilds[buildId]?.name || 'Build';
        sendNotification(
          `Build Complete: ${buildName}`,
          '✅ Completed',
          null
        );
        setWatchedBuilds(prev => {
          const next = { ...prev };
          delete next[buildId];
          return next;
        });
        // Clean up previous state
        delete previousBuildsRef.current[buildId];
      }
    });

    // Check current builds for state changes
    agents.forEach(agent => {
      if (!agent.build) return;

      const buildId = agent.build.id;
      const prevBuildState = previousBuildsRef.current[buildId];

      if (!watchedBuilds[buildId]) {
        // Still track state for builds that might be watched later
        previousBuildsRef.current[buildId] = {
          state: agent.build.state,
          status: agent.build.status,
          name: agent.build.buildType?.name || agent.build.buildTypeId,
        };
        return;
      }

      // Check if build just completed (state changed to finished)
      if (prevBuildState && prevBuildState.state === 'running') {
        const isNowFinished = agent.build.state === 'finished';
        if (isNowFinished) {
          const status = agent.build.status === 'SUCCESS' ? '✅ Success' : '❌ Failed';
          const buildName = agent.build.buildType?.name || agent.build.buildTypeId;
          sendNotification(
            `Build Complete: ${buildName}`,
            `${status}`,
            agent.build.webUrl
          );
          setWatchedBuilds(prev => {
            const next = { ...prev };
            delete next[buildId];
            return next;
          });
        }
      }

      previousBuildsRef.current[buildId] = {
        state: agent.build.state,
        status: agent.build.status,
        name: agent.build.buildType?.name || agent.build.buildTypeId,
      };
    });
  }, [agents, watchedBuilds, setWatchedBuilds]);

  // Check for test build disappearance (uses same logic as real builds)
  const prevTestBuildRef = useRef(null);

  useEffect(() => {
    // If we had a test build before and now it's gone, check if we should notify
    if (prevTestBuildRef.current && !testBuild) {
      const buildId = prevTestBuildRef.current.id;
      const prevBuildState = previousBuildsRef.current[buildId];

      // If this build was being watched and was running, send notification
      if (watchedBuilds[buildId] && prevBuildState && prevBuildState.state === 'running') {
        const buildName = watchedBuilds[buildId]?.name || 'Test Build';
        sendNotification(
          `Build Complete: ${buildName}`,
          '✅ Completed',
          null
        );
        setWatchedBuilds(prev => {
          const next = { ...prev };
          delete next[buildId];
          return next;
        });
        delete previousBuildsRef.current[buildId];
      }
    }

    // Track current test build state
    if (testBuild) {
      previousBuildsRef.current[testBuild.id] = {
        state: testBuild.state,
        status: testBuild.status,
      };
    }

    // Remember current test build for next render
    prevTestBuildRef.current = testBuild;
  }, [testBuild, watchedBuilds, setWatchedBuilds]);

  // Start a test build
  const startTestBuild = async () => {
    // Request notification permission first
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) {
      alert('Please allow notifications to test this feature.');
      return;
    }

    // Clear any existing test
    if (testBuildIntervalRef.current) {
      clearInterval(testBuildIntervalRef.current);
    }

    const testBuildId = 'test-build-' + Date.now();

    // Initialize the test build
    setTestBuild({
      id: testBuildId,
      state: 'running',
      status: 'SUCCESS',
      percentageComplete: 0,
      buildType: { name: 'Test Build' },
      'running-info': {
        percentageComplete: 0,
        elapsedSeconds: 0,
        estimatedTotalSeconds: 5,
      },
    });

    // Auto-watch the test build
    setWatchedBuilds(prev => ({
      ...prev,
      [testBuildId]: { name: 'Test Build', watchedAt: Date.now() }
    }));

    // Initialize previous state as running
    previousBuildsRef.current[testBuildId] = {
      state: 'running',
      status: 'SUCCESS',
    };

    // Progress the build
    let progress = 0;
    let elapsed = 0;
    testBuildIntervalRef.current = setInterval(() => {
      progress += 20;
      elapsed += 1;

      if (progress >= 100) {
        // Build complete - just remove it (simulates real build disappearing)
        clearInterval(testBuildIntervalRef.current);
        testBuildIntervalRef.current = null;
        setTestBuild(null);
      } else {
        setTestBuild(prev => prev ? {
          ...prev,
          percentageComplete: progress,
          'running-info': {
            ...prev['running-info'],
            percentageComplete: progress,
            elapsedSeconds: elapsed,
          },
        } : null);
      }
    }, 1000);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (testBuildIntervalRef.current) {
        clearInterval(testBuildIntervalRef.current);
      }
    };
  }, []);

  const getAgentStatusClass = (agent) => {
    if (!agent.connected) return 'disconnected';
    if (!agent.enabled) return 'disabled';
    if (agent.build) return 'building';
    return 'idle';
  };

  const getAgentStatusText = (agent) => {
    if (!agent.connected) return 'Disconnected';
    if (!agent.enabled) return 'Disabled';
    if (agent.build) return 'Building';
    return 'Idle';
  };

  const formatTimeElapsed = (build) => {
    if (build && build['running-info']) {
      const runningInfo = build['running-info'];
      const elapsedSeconds = runningInfo.elapsedSeconds;

      if (elapsedSeconds !== undefined) {
        const hours = Math.floor(elapsedSeconds / 3600);
        const minutes = Math.floor((elapsedSeconds % 3600) / 60);

        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
          return `${minutes}m`;
        } else {
          return '<1m';
        }
      }
    }
    return null;
  };

  const getShortAgentName = (fullName) => {
    // Get the last word from the agent name (split by spaces, hyphens, or underscores)
    const parts = fullName.split(/[\s\-_]+/);
    return parts[parts.length - 1] || fullName;
  };

  // Parse step info like "Step 5/14" to get percentage
  const parseStepPercentage = (build) => {
    if (build && build['running-info']) {
      const currentStageText = build['running-info'].currentStageText || build.statusText;
      if (currentStageText) {
        const match = currentStageText.match(/Step\s+(\d+)\/(\d+)/i);
        if (match) {
          const current = parseInt(match[1], 10);
          const total = parseInt(match[2], 10);
          if (total > 0) {
            return Math.round((current / total) * 100);
          }
        }
      }
    }
    // Also check statusText directly
    if (build && build.statusText) {
      const match = build.statusText.match(/Step\s+(\d+)\/(\d+)/i);
      if (match) {
        const current = parseInt(match[1], 10);
        const total = parseInt(match[2], 10);
        if (total > 0) {
          return Math.round((current / total) * 100);
        }
      }
    }
    return null;
  };

  // Get the best available percentage for a build
  const getBuildPercentage = (build) => {
    if (!build) return null;
    // First try the direct percentageComplete (if valid)
    if (build.percentageComplete !== undefined && build.percentageComplete >= 0) {
      return build.percentageComplete;
    }
    // Then try running-info percentageComplete
    if (build['running-info']?.percentageComplete !== undefined && build['running-info'].percentageComplete >= 0) {
      return build['running-info'].percentageComplete;
    }
    // Fall back to parsing step info
    return parseStepPercentage(build);
  };

  const formatTimeRemaining = (build) => {
    // Use TeamCity's running-info if available
    if (build && build['running-info']) {
      const runningInfo = build['running-info'];
      const percentageComplete = runningInfo.percentageComplete;
      const estimatedTotalSeconds = runningInfo.estimatedTotalSeconds;

      if (estimatedTotalSeconds && percentageComplete !== undefined) {
        // Calculate remaining time based on percentage and total estimated time
        const remainingSeconds = Math.max(0, Math.round((100 - percentageComplete) / 100 * estimatedTotalSeconds));

        if (remainingSeconds === 0) {
          return '0m';
        }

        const hours = Math.floor(remainingSeconds / 3600);
        const minutes = Math.floor((remainingSeconds % 3600) / 60);

        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
          return `${minutes}m`;
        } else {
          return '1m';
        }
      }
    }

    // Fallback if no running-info available
    return '-';
  };

  return (
    <div className="build-servers-container">
      <div className="build-servers-header">
        <h2>Build Servers</h2>
        <div className="build-servers-actions">
          {enableDebugging && (
            <button
              onClick={startTestBuild}
              className="test-notification-button"
              disabled={testBuild !== null}
              title="Start a test build to verify notifications"
            >
              🔔 Test
            </button>
          )}
          {lastRefresh && (
            <span className="last-refresh">
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div className="build-servers">
      {error && (
        <div className="error-message">
          Error fetching agents: {error}
        </div>
      )}

      {agents.length === 0 && !loading && !testBuild ? (
        <div className="empty-state">
          <p>No build agents found.</p>
        </div>
      ) : (
        <div className="agents-list">
          {/* Test build row */}
          {testBuild && (
            <div className="agent-row building">
              <div className="agent-name">TestAgent</div>
              <div className="agent-build-info">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${Math.min(testBuild.percentageComplete, 100)}%` }}
                  />
                  <span className="time-elapsed-overlay">
                    {formatTimeElapsed(testBuild)}
                  </span>
                  <span className="build-name-link">
                    <span className="build-name">
                      {testBuild.buildType?.name}
                    </span>
                  </span>
                  <span className="time-remaining-overlay">
                    {formatTimeRemaining(testBuild)}
                  </span>
                  <div className={`progress-icons ${watchedBuilds[testBuild.id] ? 'has-watching' : ''}`}>
                    <button
                      className={`notification-bell ${watchedBuilds[testBuild.id] ? 'watching' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleWatchBuild(testBuild.id, testBuild.buildType?.name);
                      }}
                      title={watchedBuilds[testBuild.id] ? 'Stop watching this build' : 'Notify me when this build completes'}
                    >
                      {watchedBuilds[testBuild.id] ? '🔔' : '🔕'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {agents.map((agent) => (
            <div
              key={agent.id}
              className={`agent-row ${getAgentStatusClass(agent)}`}
            >
              <a
                href={`https://azragames.teamcity.com/agentDetails.html?id=${agent.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="agent-name-link"
              >
                <div className="agent-name">{getShortAgentName(agent.name)}</div>
              </a>
              <div className="agent-build-info">
                {agent.build ? (
                  (() => {
                    const percentComplete = getBuildPercentage(agent.build);
                    if (enableDebugging) {
                      console.log(`Build ${agent.build.buildTypeId}: percentageComplete=${agent.build.percentageComplete}, running-info=`, agent.build['running-info'], `calculated=${percentComplete}`);
                    }
                    return percentComplete !== null;
                  })() ? (
                    <div className="progress-bar">
                      <div
                        className={`progress-fill ${agent.build.status === 'FAILURE' ? 'progress-fill-failure' : ''}`}
                        style={{ width: `${Math.min(getBuildPercentage(agent.build) ?? 0, 100)}%` }}
                      />
                      <span className="time-elapsed-overlay">
                        {formatTimeElapsed(agent.build)}
                      </span>
                      <a
                        href={agent.build.webUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="build-name-link"
                      >
                        <span className="build-name">
                          {agent.build.buildType?.name || agent.build.buildTypeId}
                        </span>
                      </a>
                      <span className="time-remaining-overlay">
                        {formatTimeRemaining(agent.build)}
                      </span>
                      <div className={`progress-icons ${watchedBuilds[agent.build.id] ? 'has-watching' : ''}`}>
                        {agent.build.webUrl && (
                          <a
                            href={agent.build.webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="api-icon-link"
                            title="View in TeamCity"
                          >
                            <span className="teamcity-icon">🔗</span>
                          </a>
                        )}
                        {enableDebugging && agent.build.href && (
                          <>
                            <a
                              href={`https://azragames.teamcity.com${agent.build.href}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="api-icon-link"
                              title="View build XML"
                            >
                              <span className="xml-icon">⚡</span>
                            </a>
                            <a
                              href={`https://azragames.teamcity.com${agent.build.href}/resulting-properties`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="api-icon-link"
                              title="View resolved properties"
                            >
                              <span className="properties-icon">🔧</span>
                            </a>
                          </>
                        )}
                        <button
                          className={`notification-bell ${watchedBuilds[agent.build.id] ? 'watching' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            toggleWatchBuild(agent.build.id, agent.build.buildType?.name || agent.build.buildTypeId);
                          }}
                          title={watchedBuilds[agent.build.id] ? 'Stop watching this build' : 'Notify me when this build completes'}
                        >
                          {watchedBuilds[agent.build.id] ? '🔔' : '🔕'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={agent.build.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="build-name-link"
                    >
                      <div className="build-name-no-progress">
                        {agent.build.buildType?.name || agent.build.buildTypeId}
                      </div>
                    </a>
                  )
                ) : (
                  <div className="idle-status">Idle</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Build Queue */}
      {showQueuedBuilds && queuedBuilds.length > 0 && (
        <div className="build-queue-section">
          <div className="build-queue-header">
            <h3>Queue ({queuedBuilds.length})</h3>
          </div>
          <div className="build-queue-list">
            {queuedBuilds.map((build) => (
              <div key={build.id} className={`queue-item ${watchedBuilds[build.id] ? 'has-watching' : ''}`}>
                <a
                  href={build.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="queue-build-link"
                >
                  <span className="queue-build-name">
                    {build.buildType?.name || build.buildTypeId}
                  </span>
                </a>
                {build.waitReason && (
                  <span className="queue-wait-reason" title={build.waitReason}>
                    {build.waitReason.length > 50 ? build.waitReason.substring(0, 50) + '...' : build.waitReason}
                  </span>
                )}
                <button
                  className={`notification-bell ${watchedBuilds[build.id] ? 'watching' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleWatchBuild(build.id, build.buildType?.name || build.buildTypeId);
                  }}
                  title={watchedBuilds[build.id] ? 'Stop watching this build' : 'Notify me when this build completes'}
                >
                  {watchedBuilds[build.id] ? '🔔' : '🔕'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildServers;
